/* global __litium */

import React from "react";
import { translate } from "../../Services/translation";

const TABLE_ROW_CLASS = 'variant-table__row';
const TABLE_COL_CLASS = 'variant-table__col';

/**
 * Standard table cell
 * @param children
 * @param {string} className
 * @param {string} suffix
 * @param {boolean} clean
 * @param refVar
 * @returns {JSX.Element}
 * @constructor
 */
export const Tr = ({children, className, suffix, clean = false, refVar = null}) => {
    let elClass = '';
    if (!clean) {
        elClass = TABLE_ROW_CLASS;

        if (suffix) {
            elClass += ` ${TABLE_ROW_CLASS}--${suffix}`;
        }
    }

    if (className) {
        elClass += ' ' + className;
    }

    return <tr className={elClass.trim()} ref={refVar}>{children}</tr>
}

/**
 * Standard table header cell
 *
 * @param children
 * @param {string} className
 * @param {string} suffix
 * @param {number} colSpan
 * @param {function} onClick
 * @param {boolean} clean
 * @param refVar
 * @returns {JSX.Element}
 * @constructor
 */
export const Th = ({children, className, suffix, colSpan, onClick, clean = false, refVar = null}) => {
    let elClass = '';
    if (!clean) {
        elClass = TABLE_COL_CLASS;

        if (suffix) {
            elClass += ` ${TABLE_COL_CLASS}--${suffix}`;
        }
    }

    if (className) {
        elClass += ' ' + className;
    }

    return (<th className={elClass} colSpan={colSpan} ref={refVar}>
        {clean ? (children) : <span className="variant-table__inner-cell">{children}</span>}
    </th>);
}

/**
 * Standard table cell
 * @param children
 * @param {string} className
 * @param {string} suffix
 * @param {number|null} colSpan
 * @param {string} title
 * @param {boolean} clean
 * @param refVar
 * @returns {JSX.Element}
 * @constructor
 */
export const Td = ({children, className, suffix, colSpan = null, title = null, clean = false, refVar = null}) => {
    let elClass = '';
    if (!clean) {
        elClass = TABLE_COL_CLASS;

        if (suffix) {
            elClass += ` ${TABLE_COL_CLASS}--${suffix}`;
        }
    }

    if (className) {
        elClass += ' ' + className;
    }

    return (<td className={elClass} colSpan={colSpan} ref={refVar} title={title}>
        {clean ? (children) : <span className="variant-table__inner-cell">{children}</span>}
    </td>);
}

/**
 * Tries to replicate the logic and markup of Src/Litium.Accelerator.Mvc/Views/NMAS/Shared/_StockStatus.cshtml
 *
 * @param {ProductItem} variant
 * @returns {JSX.Element}
 * @constructor
 */
export const StockStatus = ({variant}) => {
    return (variant.isInStock ?
        <span className="product__stock-status product__stock-status--in-stock">
            <link itemProp="availability" href="https://schema.org/InStock" />
            {variant.stockStatusDescription}
        </span>
        :
        <span className="product__stock-status product__stock-status--out-of-stock">
            <link itemProp="availability" href="https://schema.org/OutOfStock" />
            {variant.stockStatusDescription}
        </span>
    );
}

export const VariantMeta = ({variant}) => {
    return (
        <>
            <meta itemProp="name" content={variant.name} />
            <meta itemProp="description" content={variant.description} />
            {variant.brand != null && (<meta itemProp="brand" content={variant.brand} />)}
            {variant.price != null && variant.price.price != null && (<meta itemProp="sku" content={variant.price.price.unitOfMeasurementTitle} />)}
        </>
    );
}

/**
 * Tries to repliace the logic and markup of Src/Litium.Accelerator.Mvc/Views/Shared/DisplayTemplates/ProductPriceModel.cshtml
 *
 * @param priceModel
 * @returns {JSX.Element}
 * @constructor
 */
export const Price = ({priceModel}) => {
    if (!priceModel) return null;
    let price = priceModel.hasPrice ? priceModel.price.price : 0;
    let tieredPriceItem = null;

    const tierPriceSort = (a, b) => {
        return a.price - b.price;
    }

    if (priceModel?.campaignPrice) {
        price = priceModel?.campaignPrice.price;
    } else if (priceModel.length) {
        tieredPriceItem = priceModel.tierPrices.sort(tierPriceSort)[0];
        price = tieredPriceItem.price;
    }

    return (
        <>
            {priceModel.campaignPrice ?
                <div className="product__price-wrapper">
                    <span className="product__campaign-price">{priceModel?.campaignPrice?.price?.formatPrice()}</span>
                    <span className="product__old-price">{priceModel?.price?.price?.formatPrice()}</span>
                </div>
                :
                <>
                    <span className="price">
                        {tieredPriceItem ? (
                            <>
                                {translate('nmas.product.pricefrom')}
                                {tieredPriceItem.price.formatPrice()}
                            </>
                        ) : price > 0 && price.formatPrice()}
                    </span>
                </>
            }

            <meta itemProp="price" content={price} />
            <meta itemProp="priceCurrency" content={priceModel.currency.id} />
        </>
    );
}
